import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import { Button, Dialog, DialogActions, DialogContent } from "@mui/material";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { removeItem, updateAddressBook } from '../api/addresses';
import { AccountContext } from "../context/account";
import { CreateAddressBookDetails } from "../core/global";
import { addressShape } from "../data/form-shapes";
import { trackEvent } from '../helpers/tracking';
import styles from "../styles/components/address-book-modal.module.scss";
import { AddressBookSelect } from './address-book-select';
import { CreateForm, CreateSummary } from "./form-components";
import Loader from "./loader";
import { ModalTitle } from "./modal-title";

interface AddressBookModalProps {
  onClose: () => void
}

interface AddressBookNewModalProps {
  onClose: () => void
  id?: number | string | null
}


const blankAddress: CreateAddressBookDetails = {
  name: '',
  city: '',
  postcode: '',
  country: '',
  address: '',
  address_2: '',
  address_3: '',
  default_address: false,
}

export const AddressBookNewModal = ({
  onClose,
  id = 0
}: AddressBookNewModalProps) => {
  const [currentAdressBookId, setCurrentAdressBookId] = React.useState<null | number | string>(id)

  const {
    addressBook,
    refetchAddressBook,
  } = React.useContext(AccountContext);

  const currentAddressBook: null | CreateAddressBookDetails = React.useMemo(() => {
    if (currentAdressBookId === 0) {
      return blankAddress
    }
    if (currentAdressBookId) {
      const found = addressBook.find( item => item.id === currentAdressBookId)
      return found ? found : null
    }
    return null
  }, [currentAdressBookId, addressBook])

  return (
    <CreateForm
      title={id === 0 ? 'Create a new billing address' : 'Edit your billing address'}
      intro={id === 0 ? 'Fill in the form below to create a new billing address for your account.' : 'Update the form below to edit your billing address for your account.'}
      isModal
      shape={addressShape}
      successMessage={id === 0 ? "Your billing address has been created." : "Your billing address has been updated."}
      defaultValues={currentAddressBook}
      onCancel={() => {
        setCurrentAdressBookId(null)
        onClose()
      }}
      onSuccess={(response) => {
        if (response) {
          const {address} = response
          if (address) {
            refetchAddressBook(() => {
              const {id} = address[0] 
              setCurrentAdressBookId(id)
            })
          }
        }
      }}
      onSubmit={(vals) => {
        return updateAddressBook({...vals})
      }}
    />
  )
}


export const AddressBookModal = ({
  onClose,
}: AddressBookModalProps) => {
  const [currentAdressBookId, setCurrentAdressBookId] = useState<null | number | string>(null)
  const [isSetToDelete, setIsSetToDelete] = useState(false)
  const [isSetToEdit, setIsSetToEdit] = useState(false)

  const {
    account,
    addressBook,
    refetchAddressBook,
  } = useContext(AccountContext);

  // const useAccountOrganisation = useMemo(() => {
  //   const {organisation} = account
  //   if (organisation) {
  //     return organisation.filter( org => org.role === 'admin' || org.role === 'super-admin')
  //   }
  //   return []
  // }, [])

  useEffect(() => {
    if (account.uid) {
      trackEvent("manage-address-book-modal", {uid: account.uid})
    }
  }, [account.uid])

  const setAddressBookForEdit = useCallback((orgId: string | number | null) => {
    setCurrentAdressBookId(orgId);
    if (orgId !== null) {
      // setShowCompanySettings(true)
    }
  }, [])

  // set default address
  useEffect(() => {
    const emptyAddressBook = (addressBook && Array.isArray(addressBook) && addressBook.length === 0)
    if (!emptyAddressBook && currentAdressBookId === null) {
      const [found] = addressBook
      if (found) {
        const {id} = found
        setCurrentAdressBookId(id)
      }
    } else if (emptyAddressBook && currentAdressBookId !== "new") {
      setCurrentAdressBookId(null)
    }
  }, [addressBook, currentAdressBookId])

  const currentAddressBook: null | CreateAddressBookDetails = useMemo(() => {
    if (currentAdressBookId) {
      const found = addressBook.find( item => item.id === currentAdressBookId)
      return found ? found : null
    }
    return null
  }, [currentAdressBookId, addressBook])

  // useEffect(() => {
  //   if (addressBook && addressBook.length === 0) {
  //     setCurrentAdressBookId('new')
  //   }
  // }, [addressBook])


  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <ModalTitle onClose={onClose}>Manage your address book</ModalTitle>
      <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}
        >
        <>
          {!addressBook && <Loader center />}
        </>
        <>
        {addressBook && addressBook.length === 0 &&
          <>
            <p>You don't have any saved addresses in your address book yet. These are used for billing purpose. Click the button below to add a new address.
            </p>
            <Button 
              sx={{
                maxWidth: "240px",
              }}
              variant="contained"
              startIcon={<AddIcon sx={{color: '#ffffff'}} />}
              onClick={()=> {
                setAddressBookForEdit('new');
              }}
            >
              Create new address
            </Button>
          </>
        }
        </>
        <>
          <p>Your saved address can be selected from the drop down below.</p>
          <AddressBookSelect
            value={`${currentAdressBookId}`}
            id={'address-book-select'}
            label='Select address to edit'
            onChange={(val) => {
              setAddressBookForEdit(val);
              // if (val !== 'new') {
                // setAddressBookForEdit(id);
              // }
            }}
          />
          {currentAdressBookId !== null ? 
            <>
              {currentAdressBookId === "new"  ?
                <AddressBookNewModal
                  onClose={() => {
                    setCurrentAdressBookId(null)
                  }}
                />
              :
              <div className={styles.formWrapper}>
                <CreateSummary
                  values={currentAddressBook}
                  shape={addressShape}
                />
                <div className={styles.row}>
                  {!isSetToDelete ?
                    <Button
                      color='error'
                      onClick={() => {
                        setIsSetToDelete(true)
                      }}
                      variant="contained"
                      startIcon={<DeleteIcon sx={{color: '#ffffff'}} />}
                    >
                      Delete
                    </Button>
                    :
                    <div>
                      <Button 
                        color='error'
                        startIcon={<DeleteForeverIcon />}
                        onClick={async () => {
                          setIsSetToDelete(false)
                          await removeItem({
                            id: currentAdressBookId
                          })
                          setAddressBookForEdit(null)
                          setCurrentAdressBookId(null)
                          refetchAddressBook()
                        }}>
                        Delete forever
                      </Button>
                      <Button 
                        color="success"
                        onClick={() => {
                          setIsSetToDelete(false)
                        }}>
                        Keep
                      </Button>
                    </div>
                  }
                  
                  <Button
                    onClick={() => {
                      setIsSetToEdit(true)
                    }}
                    variant="contained"
                    startIcon={<EditIcon sx={{color: '#ffffff'}} />}
                  >
                    Edit
                  </Button>
                </div>
              </div>
            }
            </>
            :
            null
          }
          {currentAdressBookId !== null && isSetToEdit && 
            <AddressBookNewModal
              id={currentAdressBookId}
              onClose={() => {
                setIsSetToEdit(false)
                // setCurrentAdressBookId(null)
              }}
            />
          }
        </>
      </DialogContent>
      <>
        <DialogActions sx={{justifyContent: 'flex-end', pb: 3, px: 3}}>
          <Button onClick={onClose}>Close</Button>      
        </DialogActions>
      </>
    </Dialog>
  )
}


